import React, { useCallback, useContext, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { CheckCircle, Paperclip, X } from "react-feather";
import { Col, Input, InputGroup, Row } from "reactstrap";

import ChatAppContext from "../../../Chat";
import AxiosInstance from "../../../services/AxiosInstance";
import { fetchChatMembersRequest } from "../../../redux/recruiter/chat_message/chat/actions";

const SEND_ACCEPT =
  ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp,application/pdf";

const SendChat = () => {
  const [messageInput, setMessageInput] = useState("");
  const [closingTicket, setClosingTicket] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const context = useContext(ChatAppContext) || {};
  const { sendChatMessage, chatVariant, selectedUser } = context;

  const supportTicketId =
    chatVariant === "admin" && selectedUser?.chat_type === "support"
      ? parseInt(String(selectedUser?.support_ticket_id ?? ""), 10) || 0
      : 0;

  const canAttachSupport =
    (chatVariant === "admin" || chatVariant === "recruiter") && 
    (selectedUser?.chat_type === "support" || selectedUser?.support_chat === true);

  const closeSupportTicket = useCallback(async () => {
    if (supportTicketId < 1 || closingTicket) return;
    // const ok = window.confirm(
    //   `Close support ticket #${supportTicketId}? It will be marked CLOSED and the user may be notified by email.`
    // );
    // if (!ok) return;

    let adminEmail = "";
    try {
      const u = JSON.parse(localStorage.getItem("user_data") || "null");
      adminEmail = String(u?.email || u?.admin_email || "").trim();
    } catch (_) {
      adminEmail = "";
    }

    setClosingTicket(true);
    try {
      const res = await AxiosInstance().post("/admin/support/update-status", {
        ticket_id: supportTicketId,
        status: "CLOSED",
        admin_comment: "Ticket closed by admin from support chat.",
        admin_email: adminEmail || undefined,
      });
      if (!res.data?.status) {
        throw new Error(res.data?.message || "Update failed");
      }
      const closedNotice =
        `<p><strong>Your support ticket #${supportTicketId} has been closed.</strong></p>` +
        `<p class="mb-0">Thank you for contacting us. If you need more help, you can open a new ticket from the support page.</p>`;
      sendChatMessage(closedNotice);
      toast.success(res.data?.message || "Ticket closed");
      dispatch(fetchChatMembersRequest());
    } catch (e) {
      toast.error(
        e.response?.data?.message || e.message || "Could not close ticket"
      );
    } finally {
      setClosingTicket(false);
    }
  }, [supportTicketId, closingTicket, dispatch, sendChatMessage]);

  if (typeof sendChatMessage !== "function") {
    return <div>Loading...</div>;
  }

  const handleMessageChange = (message) => {
    setMessageInput(message);
  };

  const handleMessagePress = (e) => {
    const isSend = e === "send" || e.key === "Enter";
    if (!isSend) return;
    const text = messageInput.trim();
    if (!text && !attachmentFile) return;
    sendChatMessage(text || " ", attachmentFile ? { attachment: attachmentFile } : {});
    setMessageInput("");
    setAttachmentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onAttachmentChosen = (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const allowedExts = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "doc", "docx", "txt"];
    const ext = f.name.split(".").pop().toLowerCase();
    if (!allowedExts.includes(ext)) {
      toast.error("Invalid file format! Allowed formats: Images, PDF, DOC, DOCX, TXT.");
      ev.target.value = "";
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File must be 5MB or smaller");
      ev.target.value = "";
      return;
    }
    setAttachmentFile(f);
  };

  return (
    <div className="chat-message clearfix">
      <Row>
        {/* <div>
          {showEmojiPicker ? (
            <Picker set="apple" emojiSize={30} onSelect={addEmoji} />
          ) : null}
        </div> */}
        <Col xl="12" className="d-flex flex-column">
          {/* <div className="smiley-box bg-primary">
            <div className="picker" onClick={() => toggleEmojiPicker()}>
              <img src="path_to_smiley_image" alt="" />
            </div>
          </div> */}
          {attachmentFile ? (
            <div className="d-flex align-items-center gap-2 small text-muted mb-1 px-1">
              <Paperclip size={14} aria-hidden />
              <span className="text-truncate" title={attachmentFile.name}>
                {attachmentFile.name}
              </span>
              <button
                type="button"
                className="btn btn-link btn-sm text-danger p-0 ms-auto"
                onClick={() => {
                  setAttachmentFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                aria-label="Remove attachment"
              >
                <X size={16} />
              </button>
            </div>
          ) : null}
          <InputGroup className="text-box w-100">
            {canAttachSupport ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="d-none"
                  accept={SEND_ACCEPT}
                  onChange={onAttachmentChosen}
                />
                <button
                  type="button"
                  className="btn chat-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach file (support chat)"
                  aria-label="Attach file"
                >
                  <Paperclip size={18} aria-hidden />
                </button>
              </>
            ) : null}
            <Input
              type="text"
              className="form-control input-txt-bx"
              placeholder="Type a message......"
              value={messageInput}
              onKeyPress={(e) => handleMessagePress(e)}
              onChange={(e) => handleMessageChange(e.target.value)}
            />
            <button
              type="button"
              className="btn chat-send-btn"
              onClick={() => handleMessagePress("send")}
            >
              Send
            </button>
            {supportTicketId > 0 ? (
              <button
                type="button"
                className="btn chat-close-ticket-btn"
                disabled={closingTicket}
                onClick={closeSupportTicket}
                title="Mark the linked support ticket as closed"
              >
                <CheckCircle size={18} strokeWidth={2.25} aria-hidden />
                <span>{closingTicket ? "Closing…" : "Close ticket"}</span>
              </button>
            ) : null}
          </InputGroup>
        </Col>
      </Row>
    </div>
  );
};

export default SendChat;
